import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const PAID_STATUSES = ['active', 'cancel_at_period_end', 'trialing', 'past_due'];

function remainingLabel(milliseconds) {
  const totalHours = Math.max(0, Math.ceil(milliseconds / (60 * 60 * 1000)));
  if (totalHours >= 48) return `${Math.ceil(totalHours / 24)} days remaining`;
  if (totalHours >= 24) return '1 day remaining';
  return `${totalHours} hour${totalHours === 1 ? '' : 's'} remaining`;
}

function compactRemainingLabel(milliseconds) {
  const totalHours = Math.max(0, Math.ceil(milliseconds / (60 * 60 * 1000)));
  if (totalHours >= 48) return `${Math.ceil(totalHours / 24)} days left`;
  if (totalHours >= 24) return '1 day left';
  return `${totalHours}h left`;
}

export default function PromoAccessBanner({ user }) {
  const navigate = useNavigate();
  const [now, setNow] = useState(Date.now());
  const promo = user?.promoAccess;
  const isPaid = PAID_STATUSES.includes(user?.subscriptionStatus);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const remaining = useMemo(() => {
    const end = promo?.endsAt ? new Date(promo.endsAt).getTime() : 0;
    return end - now;
  }, [promo?.endsAt, now]);

  if (!promo?.redeemed || isPaid) return null;

  const active = promo.active && remaining > 0;
  const nearExpiry = active && remaining <= 24 * 60 * 60 * 1000;
  const endingToday = active && remaining < 24 * 60 * 60 * 1000;

  return (
    <Alert
      icon={false}
      severity={active ? (nearExpiry ? 'warning' : 'success') : 'info'}
      variant="standard"
      sx={{
        borderRadius: 0,
        px: { xs: 1.5, sm: 3 },
        py: { xs: 0.5, sm: 0.75 },
        position: 'sticky',
        top: { xs: 64, md: 70 },
        zIndex: 1098,
        borderBottom: '1px solid',
        borderColor: active ? (nearExpiry ? 'warning.light' : 'success.light') : 'info.light',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'nowrap' }}>
        <Box sx={{ minWidth: 0, textAlign: { xs: 'left', sm: 'center' } }}>
          <Typography component="span" fontWeight={800} sx={{ fontSize: { xs: '0.8rem', sm: '0.95rem' }, whiteSpace: 'nowrap' }}>
            {active ? (
              <>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {endingToday ? 'Your VIP preview ends today' : `VIP preview active — ${remainingLabel(remaining)}`}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {endingToday ? 'VIP preview ends today' : `VIP preview · ${compactRemainingLabel(remaining)}`}
                </Box>
              </>
            ) : 'VIP preview ended'}
          </Typography>
          <Typography component="span" sx={{ ml: 1, display: { xs: 'none', md: 'inline' }, fontSize: '0.95rem' }}>
            {active
              ? endingToday
                ? 'Start paid VIP now to keep uninterrupted access.'
                : 'Continue with VIP to keep uninterrupted access.'
              : 'Continue with VIP to unlock rankings, reviews, and advanced search again.'}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={() => navigate('/subscription')}
          sx={{
            flexShrink: 0,
            minWidth: 0,
            px: { xs: 1.25, sm: 2 },
            py: { xs: 0.35, sm: 0.6 },
            bgcolor: 'white',
            color: 'primary.main',
            fontWeight: 800,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            textTransform: 'none',
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {endingToday ? 'Start paid VIP' : 'Continue with VIP'}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Go VIP</Box>
        </Button>
      </Box>
    </Alert>
  );
}
